import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SidekickApiContext } from '../../contexts/SidekickApiContext';
import type { ArtifactLocation } from '../../hooks/useSavedMessageLookup';
import type { VisualizationImageRef } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import {
  CHART_ARTIFACT_LOOKUP_TIMEOUT_MS,
  downloadArtifact,
  downloadChartImage,
  getChartDownloadFilename,
  settleWithin,
} from '../../utils/artifact-download';
import { useSidekickPanelContext } from '../AISidekickPanel/SidekickPanelContext';
import ArtifactDownloadButton from '../ArtifactDownloadButton';
import { showSidekickErrorToast, showSidekickSuccessToast } from '../toast';
import VisualizationZoomDialog from '../VisualizationZoomDialog';
import useStyles from './MessageBubble.styles';
import VisualizationImageThumbnail from './VisualizationImageThumbnail';

type MessageVisualizationImagesProps = {
  /** Ordered server-rendered PNG refs attached to an assistant message. */
  visualizationUrls?: ReadonlyArray<VisualizationImageRef>;
  /**
   * Optional human-readable visualization title sourced from the structured
   * payload (`message.visualization.title`). Shown as the zoom dialog heading;
   * the dialog falls back to a generic label when this is absent.
   */
  title?: string;
  /** Whether M1 artifact actions are enabled. */
  isM1Enabled?: boolean;
  /**
   * Finds where the backend saved the nth chart the answer showed, counting
   * from 0 in position order. A chart it finds downloads through the
   * backend, so an expired image link does not matter.
   */
  resolveChartArtifact?: (
    chartIndex: number
  ) => Promise<ArtifactLocation | undefined>;
};

type ActiveImage = {
  url: string;
  alt: string;
};

/**
 * Renders server-produced visualization images in the order supplied
 * by BE as clickable thumbnails, opening a shared full-screen zoom
 * preview dialog when one is activated.
 */
const MessageVisualizationImages = ({
  visualizationUrls,
  title,
  isM1Enabled = false,
  resolveChartArtifact,
}: MessageVisualizationImagesProps) => {
  const { classes } = useStyles();
  const apiBaseUrl = useContext(SidekickApiContext)?.apiBaseUrl ?? '';
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const { onArtifactDownloaded } = useSidekickPanelContext();
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [activeImage, setActiveImage] = useState<ActiveImage | null>(null);

  const orderedUrls = (visualizationUrls ?? [])
    .filter(
      (image) =>
        image.url !== '' &&
        !failedUrls.has(image.url) &&
        isRenderableVisualizationUrl(image.url)
    )
    .sort((a, b) => a.position - b.position);

  if (orderedUrls.length === 0) {
    return null;
  }

  return (
    <fieldset
      aria-label={t('chat.visualization.groupAriaLabel', {
        total: orderedUrls.length,
      })}
      className={classes.visualizationImages}
      data-testid="ai-sidekick-message-visualization-images"
    >
      {orderedUrls.map((image, index) => {
        // Generic count copy is used until the backend sends per-image chart
        // title or description metadata.
        const alt = t('chat.visualization.imageAlt', {
          current: index + 1,
          total: orderedUrls.length,
        });
        return (
          <div
            className={classes.visualizationImageArtifact}
            key={`${image.position}:${image.url}`}
          >
            <VisualizationImageThumbnail
              alt={alt}
              expandAriaLabel={t('chat.visualization.preview.expandAriaLabel', {
                label: alt,
              })}
              onError={() => {
                setFailedUrls((current) => new Set(current).add(image.url));
              }}
              onExpand={() => setActiveImage({ url: image.url, alt })}
              src={image.url}
            />
            {isM1Enabled && (
              <ArtifactDownloadButton
                tooltip={t('chat.download.image.tooltip')}
                ariaLabel={t('chat.download.image.ariaLabel')}
                onDownload={async () => {
                  const artifact =
                    apiBaseUrl === ''
                      ? undefined
                      : await settleWithin(
                          resolveChartArtifact?.(
                            (visualizationUrls ?? []).filter(
                              ({ position }) => position < image.position
                            ).length
                          ),
                          CHART_ARTIFACT_LOOKUP_TIMEOUT_MS
                        );
                  if (artifact !== undefined) {
                    return downloadArtifact({
                      apiBaseUrl,
                      ...artifact,
                      fallbackFilename: getChartDownloadFilename(title),
                    });
                  }
                  return downloadChartImage({ url: image.url });
                }}
                onDownloadSuccess={() => {
                  showSidekickSuccessToast({
                    message: t('chat.download.image.successToast'),
                    closeAriaLabel: t(
                      'chat.download.successToastDismissAriaLabel'
                    ),
                    testId: 'ai-sidekick-chart-download-success-toast',
                    layout: 'artifact',
                  });
                  onArtifactDownloaded?.({
                    artifactType: 'chart',
                    position: image.position,
                  });
                }}
                onDownloadError={() => {
                  showSidekickErrorToast({
                    message: t('chat.download.errorToast'),
                    closeAriaLabel: t(
                      'chat.download.errorToastDismissAriaLabel'
                    ),
                    testId: 'ai-sidekick-chart-download-error-toast',
                    layout: 'artifact',
                  });
                }}
                testId={`ai-sidekick-chart-download-${image.position}`}
              />
            )}
          </div>
        );
      })}
      <VisualizationZoomDialog
        alt={activeImage?.alt ?? ''}
        onClose={() => setActiveImage(null)}
        open={activeImage !== null}
        src={activeImage?.url ?? ''}
        title={title}
      />
    </fieldset>
  );
};

const isRenderableVisualizationUrl = (url: string): boolean => {
  try {
    // Visualization refs are rendered as remote images; require HTTPS to
    // avoid mixed-content failures and leaking referrers to insecure origins.
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

export default MessageVisualizationImages;
